import {IOrganizationAuctionListData, PaymentResult} from '@/utils/types';
import {Box, Tooltip, Typography} from '@mui/material';
import {useEffect, useRef, useState} from 'react';
import {CAPTION, OUTLINE, SUBTITLE2} from '@/utils/constants';
import Button from '@/shared/components/Button';
import Modal from '@/shared/components/Modal';
import NeedBeLoggedModal from '../NeedBeLogged';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {getUserInfo, getUserSelector} from '@/store/user';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
import {AUCTION_ACTIVITY, PAYMENT_METHOD} from '@/utils/enums';
import {useAppDispatch} from '@/store/hooks';
import {OrganizationAttendeesService} from '@/utils/services';
import AddBidModal from './AddBidModal';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {lotItemStyle} from './LotItem.styles';
import {UserBidService} from '@/utils/services/user-bid.service';
import {getCurrentEventSelector} from '@/store/organization-event';
import Placeholder from '@/assets/images/plh.png';
import {currencyFormatter} from '@/utils/helpers/currency';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {userAPI} from '@/core/api';
import PaymentOptions from '@/pages/User/Profile/General/PaymentOptions';
import {ReactComponent as PaymentIcon} from '@icons/payment-method.svg';
import {profileContactList} from '@/pages/User/Profile/General/ProfileGeneral.style';
import {useCardAttachment, WaitResult} from '@/utils/hooks/useCardAttachment';
import axios from 'axios';

const getCardErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if ((error.response?.status ?? 0) >= 500) {
      return 'Something went wrong on our side. Please try again later.';
    }
    return getErrorMessage(error);
  }
  return error instanceof Error ? error.message : 'Something went wrong. Please try again later.';
};

const LotItem = ({
  data,
  auctionType,
  getListData,
  hasEnded,
}: {
  data: IOrganizationAuctionListData;
  auctionType?: string;
  getListData?: () => void;
  hasEnded?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const event = useSelector(getCurrentEventSelector);
  const user = useSelector(getUserSelector);

  const {isStarting, isWaiting, waitForAttachment, startAttachment} = useCardAttachment(event?.id, location.pathname);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const nameRef = useRef<HTMLDivElement | null>(null);
  const descRef = useRef<HTMLDivElement | null>(null);

  const [isNameClamped, setIsNameClamped] = useState(false);
  const [isDescClamped, setIsDescClamped] = useState(false);
  const [isBidModalOpen, setIsBidModalOpen] = useState(false);
  const [isNeedBeLoggedModalOpen, setIsNeedBeLoggedModalOpen] = useState(false);
  const [amount, setAmount] = useState<string>('');
  const [isShowPaymentModal, setIsShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState<1 | 2>(1);
  const [paymentOption, setPaymentOption] = useState(
    user?.paymentMethod ? user.paymentMethod : PAYMENT_METHOD.CREDIT_CARD
  );

  const isOffline = event?.isOnlineAuction === false;
  const isClosed = data.status === AUCTION_ACTIVITY.CLOSED || hasEnded;
  const hasBids = (data.totalBids ?? 0) > 0;

  const canPlaceBid = !!event && !isOffline && !isClosed;
  const canBuyNow = !isOffline && !isClosed && !hasBids;

  const getBidTitle = () => {
    if (isOffline) return 'Offline Auction Only';
    if (isClosed) return 'Auction Ended';
    return 'Place Bid';
  };

  useEffect(() => {
    const check = (el: HTMLElement | null) => !!el && el.scrollHeight > el.clientHeight + 1;
    setIsNameClamped(check(nameRef.current));
    setIsDescClamped(check(descRef.current));
  }, [data.name, data.lotName, data.description]);

  useEffect(() => {
    if (user?.paymentMethod) setPaymentOption(user.paymentMethod);
  }, [user?.paymentMethod]);

  // NEW: тост обробки картки
  useEffect(() => {
    if (!isWaiting) return undefined;

    const toastId = toast.loading('We are processing your card. This may take a few seconds...');

    return () => {
      toast.dismiss(toastId);
    };
  }, [isWaiting]);

  const handleBuyNowClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!user) {
      setIsNeedBeLoggedModalOpen(true);
      return;
    }

    if (!user?.paymentMethod) {
      setIsShowPaymentModal(true);
      return;
    }

    try {
      const res = await UserBidService.buyItem({
        itemId: data.id || data.itemId!,
        bidderId: user.bidderId,
        eventId: event?.id as number,
      });

      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleBidNowClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!user) {
      setIsNeedBeLoggedModalOpen(true);
    } else if (!user?.paymentMethod) {
      setIsShowPaymentModal(true);
    } else {
      setIsBidModalOpen(true);
      setAmount('');
    }
  };

  const handleAddBid = async () => {
    const numericAmount = parseFloat(amount.replace(',', '.'));
    if (!user) {
      setIsNeedBeLoggedModalOpen(true);
      return;
    }

    if (Number.isNaN(numericAmount) || numericAmount <= 0) {
      toast.error('Bid amount should be greater than 0');
      return;
    }

    if (numericAmount < data.minBid && (data.totalBids ?? 0) === 0) {
      toast.error(`Bid amount should be greater or equal to min bid ${data.minBid}`);
      return;
    }

    try {
      const dataReq = {
        itemId: data?.id || data.itemId!,
        bidderId: user?.bidderId,
        eventId: event?.id as number,
        amount: numericAmount,
        isSetByOrganizer: true,
      };

      const res = await UserBidService.createBid(dataReq);

      if (res.status === 204) {
        toast.success('Bid placed successfully');
        getListData && (await getListData());

        setIsBidModalOpen(false);
      }

      setIsBidModalOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleItemClick = () => {
    if (!user) {
      setIsNeedBeLoggedModalOpen(true);
    } else {
      navigate(`/${event?.customizeWebpage}/details/${data.id || data.itemId}`, {
        state: {from: auctionType},
      });
    }
  };

  const handleNextStep = () => setPaymentStep(2);
  const handleClosePaymentModal = () => {
    setIsShowPaymentModal(false);
    setPaymentStep(1);
  };

  // ---------- Payment: старе (не-карткові способи) ----------
  const addPaymentOption = async () => {
    if (!user?.bidderId || !user?.id) return;
    try {
      const res = await OrganizationAttendeesService.addAttendeePaymentStatus(user.bidderId, paymentOption);
      if (res.status === 204) {
        await dispatch(getUserInfo(user?.id)).unwrap();
        toast.success('Payment method added successfully!');
      }

      handleClosePaymentModal();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ---------- Payment: Stripe-флоу для картки (NEW) ----------
  const syncUser = async () => {
    if (user?.id) await dispatch(getUserInfo(user.id)).unwrap();
  };

  const handleWaitResult = async (result: WaitResult) => {
    if (result === 'attached') {
      await syncUser();
      toast.success('Payment method added successfully!', {toastId: 'card-attached'});
    }
    if (result === 'timeout') {
      toast.error('Card verification is taking longer than expected. Please refresh the page in a moment.', {
        toastId: 'card-timeout',
        autoClose: 8000,
      });
    }
  };

  const addCardViaStripe = async () => {
    try {
      const result = await startAttachment();
      if (result === 'redirect') return;

      handleClosePaymentModal();

      if (result === 'attached') {
        await syncUser();
        toast.success('Payment method is already attached');
        return;
      }

      // processing
      await handleWaitResult(await waitForAttachment());
    } catch (error) {
      handleClosePaymentModal();
      toast.error(getCardErrorMessage(error), {toastId: 'card-setup-error', autoClose: 8000});
    }
  };

  const handleConfirmPayment = () =>
    paymentOption === PAYMENT_METHOD.CREDIT_CARD ? addCardViaStripe() : addPaymentOption();

  const MAX_RETRIES = 5;

  const checkPaymentStatus = async (paymentId: string, retryCount = 0, toastId?: string | number) => {
    if (retryCount === 0) {
      toastId = toast.loading('Checking payment status...');
    }

    try {
      const {data} = await OrganizationStripeService.checkPaymentStatus(+paymentId, userAPI);

      if (data.status === PaymentResult.COMPLETED) {
        if (toastId) toast.dismiss(toastId);
        toast.success('Payment completed successfully!');
        getListData && (await getListData());
      } else if (data.status === PaymentResult.PENDING) {
        if (retryCount < MAX_RETRIES) {
          timeoutRef.current = setTimeout(() => {
            checkPaymentStatus(paymentId, retryCount + 1, toastId);
          }, 3000);
        } else {
          if (toastId) toast.dismiss(toastId);
          toast.info('Payment is still being processed. Please check the status in a few minutes.');
        }
      } else if (data.status === PaymentResult.FAILED) {
        if (toastId) toast.dismiss(toastId);
        toast.error('Payment failed!');
      } else if (data.status === PaymentResult.REFUNDED) {
        if (toastId) toast.dismiss(toastId);
        toast.error('Payment refunded!');
      }
    } catch (error) {
      if (toastId) toast.dismiss(toastId);
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get('paymentId');

    if (paymentId) {
      checkPaymentStatus(paymentId);

      params.delete('paymentId');
      params.delete('session_id');
      params.delete('organizationId');

      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
    return () => clearTimeout(timeoutRef.current);
  }, []);

  return (
    <Box>
      <Box sx={lotItemStyle.wrapper}>
        <Box sx={lotItemStyle.image} onClick={handleItemClick}>
          <img src={data.photo || Placeholder} alt="lotImage" />
        </Box>

        <Typography variant={CAPTION} sx={lotItemStyle.auctionType}>
          {data.isLiveAuction ? 'Live' : 'Silent'}
        </Typography>
        <Box sx={lotItemStyle.textWrapper}>
          <Tooltip
            title={isNameClamped ? data.name || data.lotName : ''}
            disableHoverListener={!isNameClamped}
            placement="top-start"
          >
            <Typography
              ref={nameRef}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'pointer',
                height: '52px',
              }}
              onClick={handleItemClick}
            >
              {data.name || data.lotName}
            </Typography>
          </Tooltip>
          <Typography sx={lotItemStyle.category} variant={CAPTION}>
            {data.category}
          </Typography>
          {data.isLiveAuction ? (
            <Tooltip
              title={isDescClamped ? data.description : ''}
              disableHoverListener={!isDescClamped}
              placement="top-start"
            >
              <Typography
                ref={descRef}
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 6,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: isDescClamped ? 'pointer' : 'default',
                }}
              >
                {data.description}
              </Typography>
            </Tooltip>
          ) : (
            <Box>
              <Box sx={lotItemStyle.bidWrapper}>
                <Typography sx={lotItemStyle.bid} variant={CAPTION}>
                  <Typography variant={SUBTITLE2}>{data.totalBids || 0}</Typography> Bids
                </Typography>
                <Typography sx={lotItemStyle.bid} variant={CAPTION}>
                  <Typography variant={SUBTITLE2}>{currencyFormatter.format(data.highestBid || 0)}</Typography> Current
                  bid
                </Typography>
              </Box>

              <Box sx={lotItemStyle.buttonWrapper} onClick={(e) => e.stopPropagation()}>
                {data.winnerDetails ? (
                  <Button title="Sold" variant={OUTLINE} disabled sx={lotItemStyle.button} />
                ) : (
                  <>
                    {data.buyNowPrice ? (
                      <Button
                        title={`Buy now for ${currencyFormatter.format(data.buyNowPrice)}`}
                        variant={OUTLINE}
                        onClick={handleBuyNowClick}
                        sx={lotItemStyle.button}
                        disabled={!canBuyNow}
                      />
                    ) : (
                      <Box sx={lotItemStyle.buyNowButton} />
                    )}
                    <Button
                      title={getBidTitle()}
                      onClick={handleBidNowClick}
                      sx={lotItemStyle.button}
                      disabled={!canPlaceBid}
                    />
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <Modal
        onClose={() => {
          setIsNeedBeLoggedModalOpen(false);
        }}
        open={isNeedBeLoggedModalOpen}
        sx={lotItemStyle.modalLogin}
        mainIcon={<DangerIcon />}
      >
        <NeedBeLoggedModal handleClose={() => setIsNeedBeLoggedModalOpen(false)} />
      </Modal>
      <Modal
        isShowSaveButton
        mainButtonTitle="Place Bid"
        isShowCloseButton
        closeButtonTitle="Cancel"
        onClose={() => {
          setIsBidModalOpen(false);
        }}
        open={isBidModalOpen}
        sx={lotItemStyle.modalBid}
        onSave={handleAddBid}
      >
        <AddBidModal
          amount={amount}
          setAmount={setAmount}
          totalBids={data?.totalBids || 0}
          maxBid={data?.highestBid || 0}
          bidIncrement={data?.bidIncrement || 0}
        />
      </Modal>
      {paymentStep === 1 ? (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Add Payment Method"
          description="Adding a payment method allows you to participate in the auction and make donations."
          isShowCloseButton
          closeButtonTitle="Later"
          mainButtonTitle="Add Payment Method"
          isShowSaveButton
          onSave={handleNextStep}
          mainIcon={<PaymentIcon />}
          sx={profileContactList.modal}
        />
      ) : (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Choose Payment Option"
          isShowCloseButton
          closeButtonTitle="Cancel"
          mainButtonTitle="Confirm"
          isShowSaveButton
          isSaveButtonDisable={isStarting}
          onSave={handleConfirmPayment}
          sx={profileContactList.modal}
        >
          <PaymentOptions value={paymentOption} onChange={setPaymentOption} />
        </Modal>
      )}
    </Box>
  );
};

export default LotItem;
